import type { EnvironmentApi, EnvironmentId, ScopedProjectRef } from "@cmmd-forge/contracts";
import type { ThreadRuntimeOption } from "@cmmd-forge/contracts/settings";

import { resolveThreadRuntimeSelection } from "../environments/runtime/threadSelection";
import type { Project } from "../types";

type RuntimeProjectRefCandidate = Pick<Project, "environmentId" | "id" | "repositoryIdentity">;

export type NewThreadRuntimeTarget = {
  readonly runtime: ThreadRuntimeOption;
  readonly environmentId: EnvironmentId;
  readonly api: EnvironmentApi;
};

function readTargetApi(input: {
  readonly environmentId: EnvironmentId | null;
  readonly readEnvironmentApi: (environmentId: EnvironmentId) => EnvironmentApi | null | undefined;
}): { readonly environmentId: EnvironmentId; readonly api: EnvironmentApi } | null {
  if (!input.environmentId) {
    return null;
  }
  const api = input.readEnvironmentApi(input.environmentId);
  if (!api) {
    return null;
  }
  return { environmentId: input.environmentId, api };
}

export function resolveNewThreadRuntimeTarget(input: {
  readonly requestedRuntime: ThreadRuntimeOption;
  readonly activeEnvironmentId: EnvironmentId | null;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly readEnvironmentApi: (environmentId: EnvironmentId) => EnvironmentApi | null | undefined;
}): NewThreadRuntimeTarget | null {
  const selection = resolveThreadRuntimeSelection({
    requestedDefault: input.requestedRuntime,
  });
  const targetEnvironmentId =
    selection.effectiveDefault === "cloud" ? input.primaryEnvironmentId : input.activeEnvironmentId;
  if (!targetEnvironmentId) {
    return null;
  }
  const api = input.readEnvironmentApi(targetEnvironmentId);
  if (!api) {
    return null;
  }
  return {
    runtime: selection.effectiveDefault,
    environmentId: targetEnvironmentId,
    api,
  };
}

export function resolveProjectImportRuntimeTarget(input: {
  readonly requestedRuntime: ThreadRuntimeOption;
  readonly activeEnvironmentId: EnvironmentId | null;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly readEnvironmentApi: (environmentId: EnvironmentId) => EnvironmentApi | null | undefined;
}): NewThreadRuntimeTarget | null {
  const selection = resolveThreadRuntimeSelection({
    requestedDefault: input.requestedRuntime,
  });
  const activeTarget = readTargetApi({
    environmentId: input.activeEnvironmentId,
    readEnvironmentApi: input.readEnvironmentApi,
  });
  const primaryTarget = readTargetApi({
    environmentId: input.primaryEnvironmentId,
    readEnvironmentApi: input.readEnvironmentApi,
  });
  const target =
    selection.effectiveDefault === "cloud" ? primaryTarget : (activeTarget ?? primaryTarget);
  if (!target) {
    return null;
  }
  return {
    runtime: selection.effectiveDefault,
    environmentId: target.environmentId,
    api: target.api,
  };
}

export function resolveNewThreadRuntimeProjectRef(input: {
  readonly requestedRuntime: ThreadRuntimeOption;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly projects: readonly RuntimeProjectRefCandidate[];
  readonly projectRef: ScopedProjectRef;
}): ScopedProjectRef {
  return input.projectRef;
}
